// app/javascript/components/exam-app.js
import { LitElement, html } from "lit";
import { renderQuestion } from "./quiz/quiz-question";
import { isMp4Media, resolvedMediaUrl } from "./media-utils";
import {
  completeAdmissionCancellation,
  currentAdmissionPoll,
  runSingleFlight,
  terminalAdmissionMessage,
} from "./admission-polling";
import {
  attemptHeaders,
  admissionHeaders,
  loadAttemptToken,
  loadAttemptReference,
  removeAttemptToken,
  removeAttemptReference,
  removeLegacyParticipantToken,
  storeAttemptReference,
  storeAttemptToken,
} from "./attempt-token-store";

// CSRF helpers for same-origin requests
const csrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ||
    "";
const jsonHeaders = () => ({
  "Content-Type": "application/json",
  "X-CSRF-Token": csrfToken(),
});

class ExamApp extends LitElement {
  static properties = {
    state: { type: String }, // code | details | waiting | running | summary
    loading: { type: Boolean },
    error: { type: String },
    notice: { type: String },

    // code input split
    codeA: { type: String },
    codeB: { type: String },

    // After resolve_code
    examInfo: { type: Object },

    // Details (conditionally required)
    studentFirstName: { type: String },
    studentLastName: { type: String },
    studentIdentifier: { type: String },
    reentryCapability: { type: String },

    // Run (ExamQuiz)
    run: { type: Object },
    admissionId: { type: String },
    currentAnswer: { type: String },
    timerTick: { type: Number },

    // podgląd konkretnego pytania w podsumowaniu
    showDetailIndex: { type: Number },
  };

  constructor() {
    super();
    this.state = "code";
    this.loading = false;
    this.error = null;
    this.notice = null;

    this.codeA = "";
    this.codeB = "";

    this.examInfo = null;

    this.studentFirstName = "";
    this.studentLastName = "";
    this.studentIdentifier = "";
    this.reentryCapability = "";

    this.run = null;
    this.admissionId = null;
    this._handoffAdmissionId = null;
    this.currentAnswer = null;
    this.timerTick = 0;
    this._timerId = null;
    this._statusPollId = null;
    this._admissionPollId = null;
    this._admissionPollInFlight = false;
    this._admissionPollGeneration = 0;
    this._remainingSeconds = null;
    this._timeExpiredHandling = false;

    this.showDetailIndex = null;

    this._onKeydown = this._onKeydown.bind(this);
  }

  createRenderRoot() {
    // light DOM – jak w quiz-app, żeby Tailwind działał
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeydown);
    // Legacy guest tokens were client-generated and stored under one global key.
    // They are no longer accepted after the capability-token migration.
    removeLegacyParticipantToken();

    // resume last run if exists
    const pendingAdmission = loadAttemptReference("last_exam_admission_id");
    const last = loadAttemptReference("last_exam_quiz_id");
    this._handoffAdmissionId = pendingAdmission;
    if (last) this.resumeRun(last);
    else if (pendingAdmission) this.resumeAdmission(pendingAdmission);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeydown);
    this._clearTimer();
    this._clearAdmissionPolling();
    this._clearStatusPolling();
  }

  get code() {
    return `${(this.codeA || "").padStart(3, "0")}-${(this.codeB || "").padStart(
        3,
        "0",
    )}`;
  }

  render() {
    return html`
      <div class="max-w-4xl mx-auto space-y-4">
        ${this.error ? this._renderError() : null}
        ${this.notice ? this._renderNotice() : null}

        <div class="bg-white border rounded-xl shadow-sm overflow-hidden">
          ${this._renderHeader()}
          <div class="p-5">
            ${this.state === "code" ? this._renderCodeForm() : null}
            ${this.state === "details" ? this._renderDetailsForm() : null}
            ${this.state === "waiting" ? this._renderWaiting() : null}
            ${this.state === "running" ? this._renderRunning() : null}
            ${this.state === "summary" ? this._renderSummary() : null}
          </div>
        </div>
      </div>
    `;
  }

  _renderHeader() {
    const inProgress = this.state === "running" && this.run;
    const total = this.run?.total_questions || 0;
    const idx = this.run?.current_question_index || 0;
    const hasTimeLimit = this._hasExamTimeLimit();
    const isTrial = !!(this.examInfo?.trial || this.run?.exam?.trial);

    const subtitle =
        this.state === "code"
            ? "Wpisz kod egzaminu, aby rozpocząć."
            : this.state === "details"
                ? "Uzupełnij wymagane dane, zanim rozpoczniesz egzamin."
                : this.state === "waiting"
                    ? "Poczekaj, aż egzaminator zatwierdzi Twoje zgłoszenie."
                : this.state === "running"
                    ? "Odpowiadaj na pytania po kolei."
                    : "Sprawdź swój wynik i odpowiedzi.";

    const title =
        this.examInfo?.name && this.state !== "code" ? this.examInfo.name : "Egzamin";

    return html`
      <div class="px-5 pt-4 pb-4 border-b border-gray-100 bg-gray-50/60">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold tracking-tight text-gray-900">
              ${title}
            </h1>
            <p class="mt-1 text-sm text-gray-500">
              ${subtitle}
            </p>
            ${isTrial
                ? html`
                  <div
                    class="mt-2 inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800"
                  >
                    Wersja demonstracyjna
                  </div>
                `
                : null}
          </div>

          ${inProgress
              ? html`
                <div class="flex flex-col items-end gap-2">
                  <div
                    class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 whitespace-nowrap"
                  >
                    <span
                      class="h-2 w-2 rounded-full bg-primary-500 animate-pulse"
                    ></span>
                    W trakcie egzaminu
                  </div>
                  <div class="text-xs text-gray-600">
                    Pytanie
                    <span class="font-semibold"
                      >${Math.min(idx + 1, total)}</span
                    >
                    <span class="text-gray-400">/ ${total}</span>
                  </div>
                </div>
              `
              : html`
                <div
                  class="hidden sm:flex flex-col items-end text-right text-xs text-gray-500"
                >
                  <span>Gotowy, aby rozpocząć egzamin?</span>
                </div>
              `}
        </div>

        ${this.state === "running" && hasTimeLimit
            ? html`<div class="mt-3">${this._renderTimerBar()}</div>`
            : null}
      </div>
    `;
  }

  _renderTimerBar() {
    const total = Number(this.run?.exam?.duration_seconds || 0);
    const remaining = this._currentRemainingSeconds();
    const progress = total > 0 ? Math.max(0, Math.min(100, Math.round(((total - remaining) / total) * 100))) : 0;

    return html`
      <div class="flex items-center gap-3">
        <progress class="attempt-progress h-2 flex-1" max="100" .value=${progress}>${progress}%</progress>
        <div
            class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
        >
          <span class="inline-block h-2 w-2 rounded-full bg-primary animate-pulse"></span>
          <span>Pozostało ${this._formatDuration(remaining)}</span>
        </div>
      </div>
    `;
  }

  _renderError() {
    return html`
      <div
          class="mt-2 flex items-center gap-2 rounded-md bg-red-50 text-red-800 border border-red-200 px-4 py-3 text-sm"
      >
        <span>${this.error}</span>
        <button
            class="ml-auto text-red-700 underline"
            @click=${() => (this.error = null)}
        >
          Ukryj
        </button>
      </div>
    `;
  }

  _renderNotice() {
    return html`
      <div
          class="mt-2 flex items-center gap-2 rounded-md bg-blue-50 text-blue-800 border border-blue-200 px-4 py-3 text-sm"
      >
        <span>${this.notice}</span>
        <button
            class="ml-auto text-blue-700 underline"
            @click=${() => (this.notice = null)}
        >
          Ukryj
        </button>
      </div>
    `;
  }

  _renderCodeForm() {
    return html`
      <form
        @submit=${(e) => {
      e.preventDefault();
      this.resolveCode();
    }}
        class="space-y-4"
      >
        <p class="text-gray-700 leading-relaxed">
          Aby dołączyć do egzaminu, wpisz kod przekazany przez nauczyciela.
        </p>

        <div>
          <label class="block text-sm font-medium text-gray-700">Kod egzaminu</label>
          <div class="mt-2 flex items-center gap-2">
            <input
              inputmode="numeric"
              pattern="\\d{3}"
              maxlength="3"
              class="w-24 text-center rounded-md border border-gray-300 px-3 py-2 text-lg tracking-widest shadow-sm focus:border-primary focus:ring-primary"
              placeholder="123"
              .value=${this.codeA}
              @input=${(e) => this._onCodeAInput(e)}
            />
            <span class="text-gray-400 font-semibold text-lg">-</span>
            <input
              inputmode="numeric"
              pattern="\\d{3}"
              maxlength="3"
              class="w-24 text-center rounded-md border border-gray-300 px-3 py-2 text-lg tracking-widest shadow-sm focus:border-primary focus:ring-primary"
              placeholder="456"
              .value=${this.codeB}
              @input=${(e) => this._onCodeBInput(e)}
            />
          </div>
          <p class="mt-2 text-xs text-gray-500">
            Kod ma zawsze format trzy cyfry, myślnik, trzy cyfry (np.
            <span class="font-mono">123-456</span>).
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-3 pt-2">
          <button type="submit" class="btn btn-primary" ?disabled=${this.loading}>
            Dołącz do egzaminu
          </button>
        </div>
      </form>
    `;
  }

  _onCodeAInput(e) {
    this.codeA = (e.target.value || "").replace(/\D+/g, "").slice(0, 3);
    if (this.codeA.length === 3) {
      // focus next
      const next = e.target.parentElement?.querySelectorAll("input")[1];
      next?.focus();
    }
  }

  _onCodeBInput(e) {
    this.codeB = (e.target.value || "").replace(/\D+/g, "").slice(0, 3);
  }

  async resolveCode() {
    if (this.loading) return;
    try {
      this.loading = true;
      this.error = null;
      this.notice = null;
      const code = `${this.codeA}-${this.codeB}`;
      const resp = await fetch("/api/exams/resolve_code", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ code }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok)
        throw new Error(this._humanError(data.error || "invalid_code"));
      this.examInfo = data.exam;

      // nawet jeśli dane są opcjonalne, pokaż formularz (uczeń może chcieć uzupełnić)
      this.state = "details";
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  _renderDetailsForm() {
    const info = this.examInfo || {};
    const showFirstName = !!info.first_name_required;
    const showLastName = !!info.last_name_required;
    const showStudentIdentifier = !!info.student_identifier_required;
    const visibleFieldsCount = [showFirstName, showLastName, showStudentIdentifier].filter(Boolean).length;
    const totalQuestions = Number(info.questions_count || 0);
    const passingRequired = Number(info.passing_questions_required || 0);
    const passingPercent = Number(info.passing_percent || 0);
    const durationSeconds = Number(info.duration_seconds || 0);
    const hasTimeLimit = durationSeconds > 0;
    const averageMinutesPerQuestion =
      totalQuestions > 0 && hasTimeLimit ? (durationSeconds / 60 / totalQuestions).toFixed(1) : null;

    return html`
      <form
        @submit=${(e) => {
      e.preventDefault();
      this.register();
    }}
        class="space-y-5"
      >
        ${info.trial
            ? html`
              <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                Wersja demonstracyjna: możesz podać tylko pierwszą literę imienia i nazwiska.
              </div>
            `
            : null}

        ${info.reentry_only || info.resume_only
            ? html`
              <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                ${info.reentry_only
                  ? "Okno zwykłego dołączania jest zamknięte. Ponowne wejście jest dostępne tylko dla uczestnika wskazanego przez egzaminatora."
                  : "Okno dołączania jest zamknięte. Możesz wznowić swoje trwające podejście."}
              </div>
            `
            : null}

        ${info.reentry_available
            ? html`
              <div>
                <label class="block text-sm font-medium text-gray-700">Kod ponownego wejścia</label>
                <input
                  class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-primary focus:ring-primary"
                  .value=${this.reentryCapability}
                  inputmode="numeric"
                  maxlength="9"
                  autocomplete="one-time-code"
                  placeholder="4821 7394"
                  ?required=${info.reentry_only}
                  @input=${(e) => (this.reentryCapability = e.target.value.trim())}
                />
                <p class="mt-1 text-xs text-gray-500">Kod przekazuje egzaminator po udostępnieniu ponownego wejścia.</p>
              </div>
            `
            : null}

        ${totalQuestions > 0 && passingRequired > 0
            ? html`
              <div class="rounded-md border border-primary/20 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                Egzamin zawiera <span class="font-semibold">${totalQuestions}</span> pytań.
                Do zaliczenia potrzeba
                <span class="font-semibold">${passingRequired}</span> poprawnych odpowiedzi
                (${passingPercent.toFixed(1)}%).
              </div>
            `
            : null}

        <div class="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          ${hasTimeLimit
            ? html`
                Limit czasu egzaminu: <span class="font-semibold">${this._formatDuration(durationSeconds)}</span>
                ${averageMinutesPerQuestion
                  ? html`<span class="text-gray-500">(${averageMinutesPerQuestion} min na pytanie)</span>`
                  : null}
              `
            : html`Ten egzamin jest bez limitu czasu.`}
          <div class="mt-1 text-xs text-gray-500">
            ${info.allow_back_navigation ? "Możesz wracać do poprzednich pytań." : "Nie można wracać do poprzednich pytań."}
          </div>
        </div>

        ${visibleFieldsCount > 0
            ? html`
              <div class="text-sm text-gray-600">
                Wypełnij wymagane dane uczestnika. Nauczyciel użyje ich, aby przypisać wynik do
                właściwej osoby.
              </div>

              <div class="grid gap-4 md:grid-cols-3">
                ${showFirstName
                    ? html`
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Imię</label>
                        <input
                          class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:ring-primary"
                          .value=${this.studentFirstName}
                          .maxLength=${info.trial ? 1 : 255}
                          required
                          @input=${(e) => (this.studentFirstName = info.trial ? e.target.value.slice(0, 1) : e.target.value)}
                        />
                      </div>
                    `
                    : null}
                ${showLastName
                    ? html`
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Nazwisko</label>
                        <input
                          class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:ring-primary"
                          .value=${this.studentLastName}
                          .maxLength=${info.trial ? 1 : 255}
                          required
                          @input=${(e) => (this.studentLastName = info.trial ? e.target.value.slice(0, 1) : e.target.value)}
                        />
                      </div>
                    `
                    : null}
                ${showStudentIdentifier
                    ? html`
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Nr w dzienniku</label>
                        <input
                          class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:ring-primary"
                          .value=${this.studentIdentifier}
                          maxlength="255"
                          required
                          @input=${(e) => (this.studentIdentifier = e.target.value)}
                        />
                      </div>
                    `
                    : null}
              </div>
            `
            : html`
              <div class="text-sm text-gray-600">
                Ten egzamin nie wymaga podania danych uczestnika.
              </div>
            `}

        <div class="flex flex-wrap items-center gap-3">
          <button
            type="button"
            class="btn"
            @click=${() => {
      this.state = "code";
    }}
          >
            Wstecz
          </button>
          <button type="submit" class="btn btn-primary" ?disabled=${this.loading}>
            Rozpocznij egzamin
          </button>
        </div>
      </form>
    `;
  }

  async register() {
    if (this.loading) return;
    try {
      this.loading = true;
      this.error = null;
      this.notice = null;
      const code = this.code;
      const resp = await fetch("/api/exams/register", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          code,
          student_first_name: this.studentFirstName || null,
          student_last_name: this.studentLastName || null,
          student_identifier: this.studentIdentifier || null,
          identity_configuration_token: this.examInfo?.identity_configuration_token || null,
          reentry_capability: this.reentryCapability || null,
        }),
      });
      const data = await resp.json();
      if (data.error === "exam_configuration_changed") {
        this.examInfo = null;
        this.state = "code";
      }
      if (!resp.ok || data.ok === false)
        throw new Error(this._humanError(data.error || "cannot_join"));
      if (["pending", "approved"].includes(data.status) && data.admission_token) {
        this.admissionId = data.id;
        storeAttemptToken("exam_admission", data.id, data.admission_token);
        storeAttemptReference("last_exam_admission_id", data.id);
        this.state = "waiting";
        this._setupAdmissionPolling({ immediate: data.status === "approved" });
        return;
      }
      const reusedRun = data.reused_run;
      this._startRun(data);
      if (reusedRun) {
        this.notice = "Wznowiono Twoje wcześniejsze podejście do tego egzaminu.";
      }
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  _renderWaiting() {
    return html`<div class="space-y-4 py-8 text-center">
      <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary"></div>
      <h2 class="text-xl font-semibold">Zgłoszenie czeka na wpuszczenie</h2>
      <p class="text-sm text-gray-600">Nie zamykaj tej karty. Ekran odświeży się automatycznie po decyzji egzaminatora.</p>
      <button class="btn" ?disabled=${this.loading} @click=${() => this.cancelAdmission()}>Anuluj zgłoszenie</button>
    </div>`;
  }

  async resumeAdmission(id) {
    this.admissionId = id;
    this.state = "waiting";
    this._setupAdmissionPolling({ immediate: true });
  }

  _setupAdmissionPolling({ immediate = false } = {}) {
    this._clearAdmissionPolling();
    if (immediate) this._pollAdmissionStatus();
    else this._scheduleAdmissionPoll();
  }

  _scheduleAdmissionPoll() {
    if (this.state !== "waiting" || !this.admissionId || this._admissionPollId) return;
    this._admissionPollId = window.setTimeout(() => {
      this._admissionPollId = null;
      this._pollAdmissionStatus();
    }, 3000);
  }

  _clearAdmissionPolling() {
    this._admissionPollGeneration += 1;
    if (this._admissionPollId) {
      window.clearTimeout(this._admissionPollId);
      this._admissionPollId = null;
    }
  }

  async _pollAdmissionStatus() {
    if (!this.admissionId) return;
    const requestedAdmissionId = this.admissionId;
    const requestedGeneration = this._admissionPollGeneration;
    await runSingleFlight(this, "_admissionPollInFlight", async () => {
      try {
        const resp = await fetch(`/api/exams/admissions/${requestedAdmissionId}`, {
          headers: admissionHeaders(jsonHeaders(), requestedAdmissionId),
        });
        if (!currentAdmissionPoll(this, requestedAdmissionId, requestedGeneration)) return;
        if (resp.status === 404) {
          this._leaveWaiting();
          this.error = "Nie można już odczytać tego zgłoszenia.";
          return;
        }
        if (!resp.ok) return;
        const data = await resp.json();
        if (!currentAdmissionPoll(this, requestedAdmissionId, requestedGeneration)) return;
        if (data.status === "approved" && data.run) {
          this._handoffAdmissionId = requestedAdmissionId;
          this.admissionId = null;
          this._startRun(data.run);
        } else if (["rejected", "expired", "consumed", "cancelled"].includes(data.status)) {
          this._leaveWaiting();
          this.error = terminalAdmissionMessage(data.status);
        }
      } catch (_error) {
        // A transient network failure must not discard the capability token.
      } finally {
        this.requestUpdate();
      }
    });
    this._scheduleAdmissionPoll();
  }

  _leaveWaiting() {
    if (this.admissionId) removeAttemptToken("exam_admission", this.admissionId);
    removeAttemptReference("last_exam_admission_id");
    this.admissionId = null;
    this.state = "code";
    this._clearAdmissionPolling();
  }

  async cancelAdmission() {
    if (!this.admissionId || this.loading) return;
    const admissionId = this.admissionId;
    try {
      this.loading = true;
      this._clearAdmissionPolling();
      const resp = await fetch(`/api/exams/admissions/${admissionId}`, {
        method: "DELETE",
        headers: admissionHeaders(jsonHeaders(), admissionId),
      });
      completeAdmissionCancellation(resp, () => this._leaveWaiting());
      this.notice = "Zgłoszenie zostało anulowane.";
    } catch (error) {
      this.error = error.message;
      if (this.state === "waiting" && this.admissionId === admissionId) this._scheduleAdmissionPoll();
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  _startRun(data) {
    this._clearAdmissionPolling();
    const participantToken = data.participant_token;
    delete data.participant_token;
    const previousId = loadAttemptReference("last_exam_quiz_id");
    if (previousId && previousId !== data.id) removeAttemptToken("exam_quiz", previousId);
    storeAttemptToken("exam_quiz", data.id, participantToken);
    storeAttemptReference("last_exam_quiz_id", data.id);
    this.run = data;
    this.state = "running";
    this.currentAnswer = null;
    this.showDetailIndex = null;
    this._syncTimerFromRun();
    this._setupTimer();
    this._setupStatusPolling();
    this._pollRunStatus();
  }

  async resumeRun(id) {
    try {
      this.loading = true;
      const credential = this._runRequestContext(id);
      const resp = await fetch(`/api/exams/quizzes/${id}`, {
        headers: credential.headers,
      });
      if (!resp.ok) {
        if (await this._handleAccessChangedResponse(resp, id, credential.token)) return;
        if (resp.status === 404) {
          removeAttemptToken("exam_quiz", id);
          removeAttemptReference("last_exam_quiz_id");
          if (this._handoffAdmissionId) {
            await this.resumeAdmission(this._handoffAdmissionId);
            return;
          }
        }
        throw new Error("Nie znaleziono podejścia egzaminu");
      }
      const data = await resp.json();
      this.run = data;
      this._completeAdmissionHandoff();
      if (data.status === "completed") {
        this._handleRunCompleted(data, { showReasonMessage: true });
      } else {
        this.currentAnswer = data.answers?.[String(data.current_question_index)] ?? null;
        this.state = "running";
        this._syncTimerFromRun();
        this._setupTimer();
        this._setupStatusPolling();
      }
    } catch (_e) {
      // ignore
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  _renderRunning() {
    const qRaw = this.run?.current_question || null;
    const q = qRaw || null;

    const idx = this.run?.current_question_index || 0;
    const total = this.run?.total_questions || 0;
    const busy = this.loading;
    const canGoBack = !!this.run?.exam?.allow_back_navigation && idx > 0;

    return html`
      <div class="space-y-6">
        ${renderQuestion({
          q,
          selected: this._selectedAnswerForCurrentQuestion(),
          onSelect: (opt) => {
            this.currentAnswer = opt;
            this.requestUpdate();
          },
        })}

        <div
            class="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-gray-100"
        >
          <button
              class="btn btn-muted w-full sm:w-auto"
              ?disabled=${busy}
              @click=${() => this.interrupt()}
          >
            Przerwij egzamin
          </button>

          <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            ${this.run?.exam?.allow_back_navigation
              ? html`
                  <button
                    class="btn w-full sm:w-auto"
                    ?disabled=${busy || !canGoBack}
                    @click=${() => this.prev()}
                  >
                    Poprzednie pytanie
                  </button>
                `
              : null}
            <button
                class="btn btn-primary w-full sm:w-auto"
                ?disabled=${busy}
                @click=${() => this.next()}
            >
              ${idx + 1 >= total ? "Zakończ egzamin" : "Następne pytanie"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  _renderSummary() {
    const answers = this.run?.answers || {};
    const questions = this.run?.questions || [];
    const pts = this.run?.points ?? 0;
    const max = this.run?.max_points ?? this.run?.total_questions ?? questions.length;
    const passed = !!this.run?.passed;
    const percent = max > 0 ? Math.round((pts / max) * 100) : 0;
    const passingRequired = Number(this.run?.exam?.passing_questions_required || 0);

    return html`
      <div class="space-y-6">
        <!-- Główna karta wyniku -->
        <div
            class="rounded-2xl bg-gradient-to-r from-primary-500 to-primary-400 text-white px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <div class="text-sm uppercase tracking-wide text-white/80">
              Twój wynik z egzaminu
            </div>
            <div class="mt-1 flex items-baseline gap-2">
              <span class="text-3xl font-semibold leading-none">
                ${pts} / ${max}
              </span>
              <span class="text-sm text-white/80">(${percent}%)</span>
            </div>
          </div>

          <div class="flex flex-col items-start sm:items-end gap-2">
            <span
                class="
                inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium
                ${passed
                    ? "bg-white/10 text-emerald-50 ring-1 ring-emerald-300/50"
                    : "bg-white/10 text-red-50 ring-1 ring-red-300/50"}
              "
            >
              <span
                  class="h-2 w-2 rounded-full ${passed ? "bg-emerald-300" : "bg-red-300"}"
              ></span>
              ${passed ? "Egzamin zaliczony" : "Egzamin niezaliczony"}
            </span>
            <span class="text-xs text-white/80">
              ${passingRequired > 0
                  ? `Próg zaliczenia: ${passingRequired} poprawnych odpowiedzi.`
                  : "Nauczyciel otrzyma informację o Twoim wyniku."}
            </span>
          </div>
        </div>

        <!-- Lista pytań jako kółka -->
        <div class="space-y-3">
          <div class="text-sm text-gray-700">Przegląd pytań:</div>
          <div class="flex flex-wrap gap-2">
            ${questions.map((q, i) => {
              const ans = answers[String(i)];
              const correct = q?.correct;

              // żółty = brak odp., zielony = poprawna, czerwony = błędna
              let colorClasses = "bg-yellow-100 text-yellow-800 border-yellow-200";
              if (ans) {
                colorClasses =
                    ans === correct
                        ? "bg-green-100 text-green-800 border-green-200"
                        : "bg-red-100 text-red-800 border-red-200";
              }

              return html`
                <button
                  class="
                    flex h-9 w-9 items-center justify-center rounded-full border text-xs font-medium
                    shadow-sm transition ${colorClasses}
                  "
                  @click=${() => (this.showDetailIndex = i)}
                >
                  ${i + 1}
                </button>
              `;
            })}
          </div>
        </div>

        <!-- Szczegóły pytania -->
        ${this.showDetailIndex != null ? this._renderDetail(this.showDetailIndex) : null}

        <div class="pt-2 flex flex-wrap items-center gap-3">
          <button class="btn" @click=${() => this._backToStart()}>
            Wróć do ekranu startowego
          </button>
        </div>
      </div>
    `;
  }

  _renderDetail(i) {
    const questions = this.run?.questions || [];
    const q = questions[i];
    const ans = this.run?.answers?.[String(i)];
    if (!q) return null;

    const isTF = q.correct === "T" || q.correct === "N";
    const mediaUrl = resolvedMediaUrl(q.media);

    const renderOptions = () => {
      const opts = isTF ? ["T", "N"] : ["A", "B", "C"];
      const heading = isTF ? "Odpowiedzi TAK / NIE:" : "Odpowiedzi A/B/C:";

      return html`
        <div class="mt-3 space-y-2">
          <div class="text-sm text-gray-600">${heading}</div>
          <div class="space-y-2">
            ${opts.map((opt) => {
        const label = isTF ? (opt === "T" ? "TAK" : "NIE") : opt;
        const text = isTF ? "" : q[opt.toLowerCase()] || "";

        const isCorrect = q.correct === opt;
        const isUser = ans === opt;

        const base = "flex items-start gap-3 rounded-lg px-3 py-2 text-sm";
        let state = "bg-white/80 border border-gray-200";
        if (isCorrect) state = "bg-emerald-50 border border-emerald-200";
        else if (isUser) state = "bg-red-50 border border-red-200";

        return html`
                <div class="${base} ${state}">
                  <span
                    class="
                      mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold
                      ${isCorrect
            ? "border-emerald-300 bg-emerald-100 text-emerald-800"
            : isUser
                ? "border-red-300 bg-red-100 text-red-800"
                : "border-primary/20 bg-primary-50 text-primary-700"}
                    "
                  >
                    ${isTF ? opt : label}
                  </span>
                  <div
                    class="${isCorrect
            ? "text-emerald-900"
            : isUser
                ? "text-red-900"
                : "text-gray-900"}"
                  >
                    <div class="font-medium">${label}</div>
                    ${!isTF
            ? html`
                          <div class="text-xs text-gray-700">
                            ${text ||
            html`<span class="text-gray-400 italic">(brak treści)</span>`}
                          </div>
                        `
            : null}
                  </div>
                </div>
              `;
      })}
          </div>
        </div>
      `;
    };

    return html`
      <div
        class="mt-4 rounded-2xl bg-gray-50 px-4 py-4 sm:px-5 sm:py-5 space-y-3"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="font-medium">
            Szczegóły pytania ${i + 1}${q.nr ? html` (nr ${q.nr})` : ""}
          </div>
          <button
            class="text-xs text-gray-500 hover:text-gray-700"
            @click=${() => (this.showDetailIndex = null)}
          >
            Zamknij podgląd
          </button>
        </div>

        <div class="text-sm text-gray-900">${q.question}</div>

        ${mediaUrl
        ? /\.(jpg|jpeg|png|gif)$/i.test(q.media)
            ? html`
                <div
                  class="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                >
                  <img
                    src="${mediaUrl}"
                    alt="Ilustracja do pytania"
                    class="max-h-72 w-full object-contain"
                  />
                </div>
              `
            : isMp4Media(q.media)
                ? html`
                  <div
                    class="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-black/5"
                  >
                    <video class="w-full rounded-xl" src="${mediaUrl}" controls></video>
                  </div>
                `
                : null
        : null}

        <div class="mt-2 grid gap-2 text-sm sm:grid-cols-2">
          <div class="text-gray-600">
            Poprawna odpowiedź:
            <span class="font-semibold text-gray-900">
              ${q.correct === "T" ? "TAK" : q.correct === "N" ? "NIE" : q.correct}
            </span>
          </div>

          <div class="text-gray-600">
            Twoja odpowiedź:
            <span class="font-semibold">
              ${ans === "T"
        ? "TAK"
        : ans === "N"
            ? "NIE"
            : ans ?? html`<span class="text-yellow-700">(brak)</span>`}
            </span>
          </div>
        </div>

        ${renderOptions()}

        ${q.explanation
        ? html`
              <div class="mt-3 text-sm text-gray-700">
                <span class="font-medium">Wyjaśnienie:</span>
                <span class="block mt-1 text-gray-700">${q.explanation}</span>
              </div>
            `
        : null}
      </div>
    `;
  }

  _formatDuration(totalSeconds) {
    const seconds = Math.max(0, Number(totalSeconds || 0));
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    if (mm < 60) return `${mm}:${String(ss).padStart(2, "0")}`;

    const hh = Math.floor(mm / 60);
    const rest = mm % 60;
    return `${hh}h ${rest}m`;
  }

  _backToStart() {
    removeAttemptToken("exam_quiz", this.run?.id);
    this.state = "code";
    this.run = null;
    this.notice = null;
    this.showDetailIndex = null;
    removeAttemptReference("last_exam_quiz_id");
    this._clearTimer();
    this._clearStatusPolling();
  }

  async next() {
    if (!this.run) return;
    const idx = this.run.current_question_index;
    const total = this.run.total_questions;
    if (idx + 1 >= total) return this.finish();
    await this._saveAnswer({ advance: true });
  }

  async prev() {
    if (!this.run?.exam?.allow_back_navigation) return;
    if ((this.run?.current_question_index || 0) <= 0) return;
    await this._saveAnswer({ retreat: true });
  }

  async finish() {
    const saved = await this._saveAnswer();
    if (!saved || !this.run || this.state !== "running") return;
    const runId = this.run.id;
    const credential = this._runRequestContext(runId);
    try {
      const resp = await fetch(`/api/exams/quizzes/${runId}/finish`, {
        method: "POST",
        headers: credential.headers,
      });
      if (await this._handleAccessChangedResponse(resp, runId, credential.token)) return;
      if (!resp.ok) throw new Error("Nie udało się zakończyć egzaminu");
      const data = await resp.json();
      this._handleRunCompleted(data, { showReasonMessage: false });
    } catch (e) {
      this.error = e.message;
    } finally {
      this.requestUpdate();
    }
  }

  async interrupt() {
    const runId = this.run?.id;
    if (!runId) return;
    const credential = this._runRequestContext(runId);
    try {
      const resp = await fetch(`/api/exams/quizzes/${runId}/interrupt`, {
        method: "POST",
        headers: credential.headers,
      });
      if (await this._handleAccessChangedResponse(resp, runId, credential.token)) return;
      if (!resp.ok) throw new Error("Nie udało się zapisać stanu");
      await resp.json();
      this.state = "code";
      this._clearTimer();
      this._clearStatusPolling();
      // Keep last id for resume
      storeAttemptReference("last_exam_quiz_id", this.run.id);
      this.run = null;
      this.currentAnswer = null;
      this.showDetailIndex = null;
    } catch (e) {
      this.error = e.message;
    } finally {
      this.requestUpdate();
    }
  }

  async _saveAnswer(opts = {}) {
    if (!this.run) return false;
    const runId = this.run.id;
    const credential = this._runRequestContext(runId);
    const { advance, retreat } = opts;
    try {
      const body = {};
      if (this.currentAnswer !== null && this.currentAnswer !== undefined) {
        body.answer = this.currentAnswer;
      }
      if (advance) body.advance = true;
      if (retreat) body.retreat = true;
      const resp = await fetch(`/api/exams/quizzes/${runId}`, {
        method: "PATCH",
        headers: credential.headers,
        body: JSON.stringify(body),
      });
      if (await this._handleAccessChangedResponse(resp, runId, credential.token)) return false;
      if (!resp.ok) throw new Error("Nie udało się zapisać odpowiedzi");
      const data = await resp.json();
      this.run = data;
      this._completeAdmissionHandoff();
      this._syncTimerFromRun();
      if (data.status === "completed") {
        this._handleRunCompleted(data, { showReasonMessage: true });
      }
      this.currentAnswer = null;
      return true;
    } catch (e) {
      this.error = e.message;
      return false;
    } finally {
      this.requestUpdate();
    }
  }

  _selectedAnswerForCurrentQuestion() {
    if (this.currentAnswer !== null && this.currentAnswer !== undefined) {
      return this.currentAnswer;
    }

    const idx = this.run?.current_question_index;
    if (idx === null || idx === undefined) return null;
    return this.run?.answers?.[String(idx)] ?? null;
  }

  _setupTimer() {
    this._clearTimer();
    if (!this._hasExamTimeLimit()) return;
    this._syncTimerFromRun();
    this._timerId = window.setInterval(() => {
      this.timerTick++;
      if (this.state !== "running") return;
      if (typeof this._remainingSeconds === "number") {
        this._remainingSeconds = Math.max(0, this._remainingSeconds - 1);
      }

      if (this._currentRemainingSeconds() <= 0) {
        this._handleTimeExpired();
        return;
      }

      this.requestUpdate();
    }, 1000);
  }

  _hasExamTimeLimit() {
    return Number(this.run?.exam?.duration_seconds || 0) > 0;
  }

  _currentRemainingSeconds() {
    if (!this._hasExamTimeLimit()) return 0;
    if (typeof this._remainingSeconds === "number") {
      return Math.max(0, this._remainingSeconds);
    }

    const fromPayload = Number(this.run?.time_remaining_seconds);
    if (Number.isFinite(fromPayload)) return Math.max(0, fromPayload);

    const duration = Number(this.run?.exam?.duration_seconds || 0);
    const passed = Number(this.run?.seconds_passed || 0);
    return Math.max(0, duration - passed);
  }

  _syncTimerFromRun() {
    if (!this._hasExamTimeLimit()) {
      this._remainingSeconds = null;
      return;
    }

    const fromPayload = Number(this.run?.time_remaining_seconds);
    this._remainingSeconds = Number.isFinite(fromPayload) ? Math.max(0, fromPayload) : this._currentRemainingSeconds();
  }

  _clearTimer() {
    if (this._timerId) {
      window.clearInterval(this._timerId);
      this._timerId = null;
    }
    this._remainingSeconds = null;
    this._timeExpiredHandling = false;
  }

  _setupStatusPolling() {
    this._clearStatusPolling();
    if (!this.run?.id) return;
    this._statusPollId = window.setInterval(() => {
      this._pollRunStatus();
    }, 5000);
  }

  _clearStatusPolling() {
    if (this._statusPollId) {
      window.clearInterval(this._statusPollId);
      this._statusPollId = null;
    }
  }

  async _pollRunStatus() {
    if (this.state !== "running" || !this.run?.id) return;
    const runId = this.run.id;
    const credential = this._runRequestContext(runId);

    try {
      const resp = await fetch(`/api/exams/quizzes/${runId}`, {
        headers: credential.headers,
      });
      if (await this._handleAccessChangedResponse(resp, runId, credential.token)) return;
      if (!resp.ok) return;
      const data = await resp.json();
      this.run = data;
      this._completeAdmissionHandoff();
      this._syncTimerFromRun();

      if (data.status === "completed") {
        this._handleRunCompleted(data, { showReasonMessage: true });
      } else {
        this.requestUpdate();
      }
    } catch (_e) {
      // silent polling failure
    }
  }

  _finishReasonMessage(reason) {
    switch (reason) {
      case "closed_by_examiner":
        return "Egzamin został zakończony przez egzaminatora.";
      case "time_elapsed":
        return "Czas egzaminu upłynął. Egzamin został automatycznie zakończony.";
      default:
        return null;
    }
  }

  _completeAdmissionHandoff() {
    if (!this._handoffAdmissionId) return;

    removeAttemptToken("exam_admission", this._handoffAdmissionId);
    removeAttemptReference("last_exam_admission_id");
    this._handoffAdmissionId = null;
  }

  async _handleAccessChangedResponse(response, runId, requestedToken) {
    if (response.status !== 409) return false;

    let payload;
    try {
      payload = await response.clone().json();
    } catch (_error) {
      return false;
    }
    if (payload?.error !== "access_changed") return false;

    const currentId = this.run?.id;
    const storedId = loadAttemptReference("last_exam_quiz_id");
    if ((currentId && currentId !== runId) || (!currentId && storedId && storedId !== runId)) return true;
    if (loadAttemptToken("exam_quiz", runId) !== requestedToken) return true;

    removeAttemptToken("exam_quiz", runId);
    removeAttemptReference("last_exam_quiz_id");
    this.run = null;
    this.state = "code";
    this.currentAnswer = null;
    this.showDetailIndex = null;
    this.notice = null;
    this.error = "Dostęp do tego podejścia został zmieniony przez egzaminatora. Wpisz kod egzaminu ponownie, aby kontynuować.";
    this._clearTimer();
    this._clearStatusPolling();
    this.requestUpdate();
    return true;
  }

  _handleRunCompleted(data, { showReasonMessage = true } = {}) {
    this.run = data;
    this.state = "summary";
    this.showDetailIndex = null;
    this.currentAnswer = null;
    this._clearTimer();
    this._clearStatusPolling();

    if (showReasonMessage) {
      const message = this._finishReasonMessage(data?.finished_reason);
      if (message) this.notice = message;
    }
  }

  async _handleTimeExpired() {
    if (this._timeExpiredHandling) return;
    this._timeExpiredHandling = true;
    if (!this.run || this.state !== "running") return;

    try {
      const saved = await this._saveAnswer();
      if (!saved || !this.run || this.state !== "running") return;
      if (this.run?.status === "completed") return;

      const runId = this.run.id;
      const credential = this._runRequestContext(runId);
      const finishResp = await fetch(`/api/exams/quizzes/${runId}/finish`, {
        method: "POST",
        headers: credential.headers,
      });
      if (await this._handleAccessChangedResponse(finishResp, runId, credential.token)) return;
      if (!finishResp.ok) throw new Error("Nie udało się zakończyć egzaminu po czasie");

      const finished = await finishResp.json();
      this._handleRunCompleted(finished, { showReasonMessage: true });
    } catch (e) {
      this.error = e.message;
      this._timeExpiredHandling = false;
    } finally {
      this.requestUpdate();
    }
  }

  _onKeydown(event) {
    if (event.altKey || event.metaKey || event.ctrlKey) return;

    const key = event.key.toLowerCase();

    // While in code form, Enter submits
    if (this.state === "code") {
      if (key === "enter") {
        event.preventDefault();
        this.resolveCode();
      }
      return;
    }

    // In details form, Enter submits
    if (this.state === "details") {
      if (key === "enter") {
        event.preventDefault();
        this.register();
      }
      return;
    }

    if (this.state === "running" && this.run?.current_question) {
      const q = this.run.current_question;
      const isTF = q.kind === "tf" || q.kind === "boolean";

      if (key === "escape") {
        event.preventDefault();
        this.interrupt();
        return;
      }

      if (isTF) {
        if (key === "t" || key === "1") {
          event.preventDefault();
          this.currentAnswer = "T";
          this.requestUpdate();
          return;
        }
        if (key === "n" || key === "2") {
          event.preventDefault();
          this.currentAnswer = "N";
          this.requestUpdate();
          return;
        }
      } else {
        if (key === "1" || key === "a") {
          event.preventDefault();
          this.currentAnswer = "A";
          this.requestUpdate();
          return;
        }
        if (key === "2" || key === "b") {
          event.preventDefault();
          this.currentAnswer = "B";
          this.requestUpdate();
          return;
        }
        if (key === "3" || key === "c") {
          event.preventDefault();
          this.currentAnswer = "C";
          this.requestUpdate();
          return;
        }
      }

      if (key === "enter" || key === " ") {
        event.preventDefault();
        this.next();
      }
    }

    if (this.state === "summary") {
      if (key === "enter" || key === " ") {
        event.preventDefault();
        this._backToStart();
      }
    }
  }

  _runHeaders(id = this.run?.id) {
    return attemptHeaders(jsonHeaders(), "exam_quiz", id);
  }

  _runRequestContext(id = this.run?.id) {
    return {
      token: loadAttemptToken("exam_quiz", id),
      headers: this._runHeaders(id),
    };
  }

  _humanError(code) {
    // Map znanych kodów błędów na komunikaty dla użytkownika.
    const map = {
      invalid_code_format: "Nieprawidłowy format kodu.",
      not_found:
          "Nie znaleziono dostępnego egzaminu o podanym kodzie. Jeśli egzamin był już rozpoczęty, kontynuuj w otwartej karcie albo poproś egzaminatora o użycie opcji „Wpuść ponownie”.",
      window_closed: "Egzamin nie jest dostępny w tym czasie.",
      exam_inactive: "Egzamin jest nieaktywny.",
      first_name_required: "Imię jest wymagane.",
      last_name_required: "Nazwisko jest wymagane.",
      student_identifier_required: "Numer w dzienniku jest wymagany.",
      exam_configuration_changed: "Ustawienia egzaminu zostały zmienione. Wpisz kod ponownie.",
      trial_limit:
          "To jest wersja demonstracyjna egzaminu. Możesz podać tylko pierwszą literę imienia i nazwiska.",
      already_started:
          "Ten egzamin jest już rozpoczęty. Jeśli masz nadal otwartą kartę z egzaminem, kontynuuj w niej. Jeśli nie masz już do niej dostępu, poproś egzaminatora o użycie opcji „Wpuść ponownie”.",
      already_completed:
          "To podejście do egzaminu zostało już wcześniej zakończone. Nie można rozpocząć go ponownie.",
    };

    // Normalizacja wejścia (na wypadek null/undefined/obiektów)
    const raw = typeof code === "string" ? code.trim() : "";

    // Jeśli to znany kod — zwracamy bez dyskusji.
    if (raw && Object.prototype.hasOwnProperty.call(map, raw)) {
      return map[raw];
    }

    return "Nie udało się wykonać operacji.";
  }
}

customElements.define("exam-app", ExamApp);
